import { z } from 'zod';

import { NotificationKindSchema } from '../enums';
import { IdSchema } from './common';

/**
 * Уведомления (BLUEPRINT Stage 8, задачи 2 и 7): фильтры журнала отправок в админке
 * и ручная рассылка `CUSTOM` отряду или участнику. Сам DTO строки собирает API.
 */

/** Что стало с уведомлением: поставлено в очередь, отправлено или упало с ошибкой. */
export const NotificationStateSchema = z.enum(['queued', 'sent', 'failed']);
export type NotificationState = z.infer<typeof NotificationStateSchema>;

/**
 * Темы сообщений вида `CUSTOM` — по ключу дедупликации (`topicOf` в API; раунд 4, O14). Подписи —
 * `texts.notifications.topics` админки (тип требует подпись у каждой темы), фильтр «Тема» журнала.
 * Stage 17, раунд персон 1, O1: темы заявок (`order:expired` … `order:guest_left_captain`),
 * разовый сбор ops (`ops-extra:created`), сводки и продление.
 */
export const NOTIFICATION_TOPICS = [
  'manual',
  'group',
  'order:paid',
  'order:paid_late',
  'order:refund',
  'order:expired',
  'order:ops_cancelled',
  'order:item_cancelled',
  'order:unfulfilled',
  'order:seat_taken',
  'order:guest_captain',
  'order:guest_left_captain',
  'season:launched',
  'season:failed',
  'season:paused',
  'season:renewal_seats',
  'season:renewal_short',
  'seat:opened',
  'seat:expired',
  'gathering:short',
  'gathering:short_ops',
  'gathering:no_show',
  'gathering:move_request',
  'tournament:assigned',
  'extra:created',
  'ops-extra:created',
  'pool:longwait',
  'pool:transfer',
  'venue:booking',
  'venue:declined',
  'venue:restored',
  'venue:reminder',
  'flag:resolved',
  'proposal:resolved',
  'referral:earned',
  'reserve:accepted',
  'level',
  'digest',
  'report',
  'queue-alert',
] as const;
export const NotificationTopicSchema = z.enum(NOTIFICATION_TOPICS);
export type NotificationTopic = z.infer<typeof NotificationTopicSchema>;

export const AdminNotificationsListInputSchema = z.object({
  kind: z.array(NotificationKindSchema).max(10).optional(),
  /** Тема сообщения `CUSTOM` (O1): фильтр по ключу дедупликации */
  topic: NotificationTopicSchema.optional(),
  state: NotificationStateSchema.optional(),
  userId: IdSchema.optional(),
  squadId: IdSchema.optional(),
  /** Имя, username или Telegram ID получателя */
  search: z.string().trim().max(60).optional(),
  cursor: IdSchema.optional(),
  limit: z.number().int().min(1).max(100).default(50),
});
export type AdminNotificationsListInput = z.infer<typeof AdminNotificationsListInputSchema>;

/** Куда уходит ручное сообщение: в личку каждому участнику отряда, в группу отряда или одному. */
export const NotifyTargetSchema = z.enum(['squad', 'squadChat', 'user']);
export type NotifyTarget = z.infer<typeof NotifyTargetSchema>;

export const AdminNotifySendInputSchema = z
  .object({
    target: NotifyTargetSchema,
    squadId: IdSchema.optional(),
    userId: IdSchema.optional(),
    text: z.string().trim().min(2, 'Напиши текст').max(2000),
  })
  .refine((v) => (v.target === 'user' ? Boolean(v.userId) : Boolean(v.squadId)), {
    message: 'Укажи получателя',
  });
export type AdminNotifySendInput = z.infer<typeof AdminNotifySendInputSchema>;

/**
 * Результат ручной рассылки: ушло сразу, поставлено в очередь, пропущено дедупликацией,
 * упало. С включённой очередью почти всё попадает в `queued` — итог виден в журнале.
 */
export const AdminNotifySendResultSchema = z.object({
  sent: z.number().int().nonnegative(),
  queued: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type AdminNotifySendResult = z.infer<typeof AdminNotifySendResultSchema>;

/** Состояние очередей для `/health` и панели админки. */
export const QueueStatsSchema = z.object({
  name: z.string(),
  waiting: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  delayed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type QueueStats = z.infer<typeof QueueStatsSchema>;
